import Icon, { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { ExpandDown } from '../../../../assets/CustomIcons/ExpandDown';
import { ExpandRight } from '../../../../assets/CustomIcons/ExpandRight';
import { RichTextEditor } from '../../../../pages/Task/components/editor/RichTextEditor';
import { SubtaskSuggestion } from '../../../../types/AI';
import Task from '../../../../types/Task';
import { TaskType } from '../../../../types/TaskType';
import CustomTooltip from '../../../UI/Tooltip/CustomTooltip';

// Helper functions for localStorage
const getBreakdownShownKey = (taskId: number): string => {
  return `taskonio_ai_breakdown_shown_${taskId}`;
};

const hasBreakdownBeenShown = (taskId: number): boolean => {
  try {
    const key = getBreakdownShownKey(taskId);
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.warn('Failed to check breakdown shown status:', error);
    return false;
  }
};

const markBreakdownAsShown = (taskId: number): void => {
  try {
    const key = getBreakdownShownKey(taskId);
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.warn('Failed to mark breakdown as shown:', error);
  }
};

interface AIBreakdownTableRow extends SubtaskSuggestion {
  index: number;
}

interface AIBreakdownTableProps {
  task: Task;
  subtasks: SubtaskSuggestion[];
  selectedIndices: number[];
  onSelectionChange: (indices: number[]) => void;
  onSubtaskUpdate?: (index: number, updates: Partial<SubtaskSuggestion>, skipHistory?: boolean) => void;
  onAddSubtask?: () => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
  disabled?: boolean;
  isLoading?: boolean;
}

export const AIBreakdownTable: React.FC<AIBreakdownTableProps> = ({
  task,
  subtasks,
  selectedIndices,
  onSelectionChange,
  onSubtaskUpdate,
  onAddSubtask,
  usersWithAccessToSpace = [],
  disabled = false,
  isLoading = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const [editingTitleIndex, setEditingTitleIndex] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');

  const rows: AIBreakdownTableRow[] = subtasks.map((subtask, index) => ({
    ...subtask,
    index,
  }));

  const handleRowSelection = (index: number, checked: boolean) => {
    if (isLoading) return;
    if (checked) {
      onSelectionChange([...selectedIndices, index]);
    } else {
      onSelectionChange(selectedIndices.filter((i) => i !== index));
    }
  };

  const toggleRowExpanded = (index: number) => {
    if (isLoading) return;
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleTitleClick = (index: number, currentTitle: string) => {
    if (isLoading) return;
    setEditingTitleIndex(index);
    setEditingTitle(currentTitle);
  };

  const handleTitleSave = () => {
    if (isLoading) return;
    if (editingTitleIndex !== null && editingTitle.trim()) {
      onSubtaskUpdate?.(editingTitleIndex, { title: editingTitle });
    }
    setEditingTitleIndex(null);
    setEditingTitle('');
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (isLoading) return;
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditingTitleIndex(null);
      setEditingTitle('');
    }
  };

  // Auto-expand first task with description on initial load (description discovery)
  // Only do this once per task (tracked in localStorage)
  useEffect(() => {
    if (!hasAutoExpanded && subtasks.length > 0) {
      // Check if we've already shown the breakdown for this task
      const alreadyShown = hasBreakdownBeenShown(task.id);

      if (!alreadyShown) {
        // Find the first task with a non-empty description
        const firstTaskWithDescription = subtasks.findIndex(
          (subtask) => subtask.description && subtask.description.trim().length > 0
        );

        if (firstTaskWithDescription !== -1) {
          setExpandedRows(new Set([firstTaskWithDescription]));
          setHasAutoExpanded(true);
          // Mark as shown in localStorage so it doesn't auto-expand next time
          markBreakdownAsShown(task.id);
        }
      }
    }
  }, [subtasks, hasAutoExpanded, task.id]);

  return (
    <div className="w-full flex flex-col">
      <style>{`
        .ai-breakdown-checkbox .ant-checkbox-inner {
          border-radius: 2px;
        }
        .ai-breakdown-checkbox .ant-checkbox-checked .ant-checkbox-inner {
          background-color: #388bff !important;
          border-color: #388bff !important;
        }
        .ai-breakdown-checkbox .ant-checkbox-checked:after {
          border-color: #388bff !important;
        }
        .ai-breakdown-checkbox .ant-checkbox-checked:hover .ant-checkbox-inner {
          background-color: #388bff !important;
          border-color: #388bff !important;
        }
        .ai-breakdown-checkbox .ant-checkbox:hover .ant-checkbox-inner {
          border-color: #388bff !important;
        }
      `}</style>

      <div className="flex justify-between px-4 pt-4 pb-[10px]">
        <div className="flex items-center gap-1">
          <div className="text-brand-grey-dark text-xs font-semibold">
            {task.type === TaskType.epic ? 'Items' : 'Subitems'}
          </div>
          {/* <Button
            type="link"
            className="h-6 ml-1 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-start items-center gap-1 inline-flex"
            onClick={() => !isLoading && onAddSubtask?.()}
            disabled={disabled}
          >
            <PlusOutlined className="text-neutral-900 hover:text-brand-taskonio-green p-1" />
          </Button> */}
        </div>
        <CustomTooltip title="Your original description will remain unchanged for everyone, until you save the changes.">
          <div className="flex gap-1 text-xs font-normal bg-neutral-200 text-neutral-800 p-1 rounded cursor-default">
            Draft Mode
            <InfoCircleOutlined />
          </div>
        </CustomTooltip>
      </div>

      {/* Body Rows */}
      {rows.map((row) => {
        const isSelected = selectedIndices.includes(row.index);
        const isExpanded = expandedRows.has(row.index);

        return (
          <div key={row.index}>
            {/* Row Header */}
            <div
              className={`flex items-center w-full min-h-[40px] border-b border-neutral-200 transition-colors ${
                isSelected ? 'bg-blue-100' : 'bg-white hover:bg-neutral-50'
              }`}
            >
              {/* Checkbox */}
              <div className="flex items-center justify-center py-2.5 min-w-[32px] flex-shrink-0 border-r border-neutral-200 ai-breakdown-checkbox">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => handleRowSelection(row.index, e.target.checked)}
                  disabled={disabled}
                  style={isLoading ? { pointerEvents: 'none' } : undefined}
                />
              </div>

              {/* Collapse/Expand Button */}
              <Button
                size="small"
                className="px-[3px] text-neutral-900"
                onClick={() => toggleRowExpanded(row.index)}
                disabled={disabled}
                style={isLoading ? { pointerEvents: 'none' } : undefined}
              >
                {isExpanded ? <Icon component={ExpandDown} /> : <Icon component={ExpandRight} />}
              </Button>

              {/* Title */}
              <div className="flex items-center flex-1 min-w-0 px-1 py-2">
                {editingTitleIndex === row.index ? (
                  <Input
                    value={editingTitle}
                    onChange={(e) => !isLoading && setEditingTitle(e.target.value)}
                    onBlur={handleTitleSave}
                    onKeyDown={handleTitleKeyDown}
                    autoFocus
                    disabled={disabled}
                    className="h-6 text-sm font-normal leading-5 text-neutral-1000 px-[3px]"
                    style={isLoading ? { pointerEvents: 'none' } : undefined}
                  />
                ) : (
                  <div
                    className={`flex items-center h-6 px-1 text-sm font-normal leading-5 text-neutral-1000 overflow-hidden text-ellipsis whitespace-nowrap ${
                      disabled || isLoading ? 'cursor-not-allowed' : 'cursor-text'
                    }`}
                    onClick={() => !disabled && !isLoading && handleTitleClick(row.index, row.title)}
                  >
                    {row.title}
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Description */}
            {isExpanded && (
              <div className="bg-white border-b border-neutral-200 px-4 py-3">
                <div className="text-xs font-semibold text-neutral-700 mb-2">Description</div>
                <div className="ml-0" style={isLoading ? { pointerEvents: 'none' } : undefined}>
                  <RichTextEditor
                    value={row.description || ''}
                    onChange={(newDescription) => {
                      if (!isLoading) {
                        // Update directly, skip history to avoid polluting undo/redo
                        onSubtaskUpdate?.(row.index, { description: newDescription }, true);
                      }
                    }}
                    usersWithAccessToSpace={usersWithAccessToSpace}
                    placeholder="Add description..."
                    minHeight="300px"
                    mode="description"
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
